import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { TrendingUp, CheckCircle2 } from 'lucide-react';
interface SlideFinancialProjectionsProps {
  onContinue: () => void;
}
const YEARS = [{
  year: '2026',
  label: 'Expectation',
  revenue: 3.9,
  grossMargin: 2.93,
  netResult: -2.95
}, {
  year: '2027',
  label: 'Budget',
  revenue: 12.82,
  grossMargin: 9.62,
  netResult: 0.35,
  profitable: true
}, {
  year: '2028',
  label: 'Budget',
  revenue: 22.75,
  grossMargin: 18.2,
  netResult: 5.45,
  profitable: true
}, {
  year: '2029',
  label: 'Budget',
  revenue: 36.0,
  grossMargin: 30.6,
  netResult: 18.6,
  profitable: true
}];
export function SlideFinancialProjections({
  onContinue
}: SlideFinancialProjectionsProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 250);
    const t2 = setTimeout(() => setStage(2), 700);
    const t3 = setTimeout(() => setStage(3), 1300);
    const t4 = setTimeout(() => setStage(4), 2200);
    return () => {
      ;
      [t1, t2, t3, t4].forEach(clearTimeout);
    };
  }, []);
  const chartW = 720;
  const chartH = 300;
  const padL = 50;
  const padR = 28;
  const padT = 30;
  const padB = 36;
  const innerW = chartW - padL - padR;
  const innerH = chartH - padT - padB;
  const yMin = -5;
  const yMax = 40;
  const yToPx = (v: number) => padT + innerH - (v - yMin) / (yMax - yMin) * innerH;
  const colXs = YEARS.map((_, i) => padL + innerW * (i + 0.5) / YEARS.length);
  const barWidth = innerW / YEARS.length * 0.5;
  const zeroY = yToPx(0);
  const yTicks = [0, 10, 20, 30, 40];
  const netLinePath = YEARS.map((y, i) => `${i === 0 ? 'M' : 'L'} ${colXs[i]} ${yToPx(y.netResult)}`).join(' ');
  return <div className="h-full w-full flex flex-col px-8 py-7 md:px-16 md:py-9 max-w-[1400px] mx-auto" data-id="element-474" data-mp="368zyg4i0bnwz">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center space-y-4" data-id="element-475" data-mp="3bafmm5fheajl">
        <h2 className="text-3xl md:text-5xl font-sans font-bold text-white tracking-tight leading-tight" data-id="element-476" data-mp="2gnqmbauhjof1">
          Financial Projections
        </h2>
        <p className="text-teal-400 text-base md:text-lg font-medium mt-1" data-id="element-477" data-mp="2qkv839kf5hvb">
          2026 – 2029 · Path to scale
        </p>
      </motion.div>

      {/* Stat tiles */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-id="element-478" data-mp="1xvejkmzx6l4d">
        {[{
        label: '2026 Expected Revenue',
        value: 'DKK 3.9M',
        sub: 'Starting point',
        tone: 'navy'
      }, {
        label: '2027 Budget Revenue',
        value: 'DKK 12.82M',
        sub: '3.3× vs 2026',
        tone: 'dark'
      }, {
        label: 'Breakeven from',
        value: '2027',
        sub: 'Net result +DKK 0.35M',
        tone: 'green',
        icon: <CheckCircle2 className="w-4 h-4" data-id="element-479" data-mp="3gvdon5f83t8s" />
      }, {
        label: '2029 Revenue Target',
        value: 'DKK 36M',
        sub: 'Net result +DKK 18.6M',
        tone: 'teal',
        icon: <TrendingUp className="w-4 h-4" data-id="element-480" data-mp="ibx3dsw353ol" />
      }].map((s, i) => {
        const isTeal = s.tone === 'teal';
        const isGreen = s.tone === 'green';
        const isDark = s.tone === 'dark';
        return <div key={s.label} className={`rounded-xl p-4 border ${isTeal ? 'bg-teal-400/10 border-teal-400/40' : isGreen ? 'bg-emerald-500/5 border-emerald-400/30' : isDark ? 'bg-navy-950 border-navy-700' : 'bg-navy-900/60 border-navy-700'}`} data-id="element-481" data-mp="8czhyytyhq9t" data-mpi={i}>
              <div className="flex items-center gap-1.5" data-id="element-482" data-mp="39bmo01vmlmp0">
                {s.icon && <span className={isTeal ? 'text-teal-400' : isGreen ? 'text-emerald-400' : 'text-white/45'} data-id="element-483" data-mp="34ko3sr7io4gh">
                    {s.icon}
                  </span>}
                <p className="text-white/45 text-[9px] uppercase tracking-widest font-bold" data-id="element-484" data-mp="3s7w8p8119bhz">
                  {s.label}
                </p>
              </div>
              <p className={`text-xl md:text-2xl font-bold tabular-nums mt-1 ${isTeal ? 'text-teal-400' : isGreen ? 'text-emerald-400' : 'text-white'}`} data-id="element-485" data-mp="3iarkz3rjh7z2">
                {s.value}
              </p>
              <p className={`text-[10px] mt-0.5 ${isTeal ? 'text-teal-300/80' : isGreen ? 'text-emerald-300/80' : 'text-white/50'}`} data-id="element-486" data-mp="26xyv5yib6tln">
                {s.sub}
              </p>
            </div>;
      })}
      </motion.div>

      {/* Chart card */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 2 ? 1 : 0,
      y: stage >= 2 ? 0 : 20
    }} transition={{
      duration: 0.5
    }} className="flex-1 mt-4 bg-navy-900/60 border border-teal-400/20 rounded-2xl p-5 md:p-6 flex flex-col min-h-0" data-id="element-487" data-mp="mf4tamzcab4v">
        <div className="flex items-center justify-between pb-3 border-b border-teal-400/20" data-id="element-488" data-mp="161szhvsmw6i4">
          <div className="flex items-center gap-2" data-id="element-489" data-mp="1a3xsqtf67148">
            <div className="w-6 h-6 rounded-md bg-teal-400 flex items-center justify-center" data-id="element-490" data-mp="1a235h8gfewwz">
              <div className="w-2.5 h-2.5 rounded-full bg-white" data-id="element-491" data-mp="1f16cuzjvpf84" />
            </div>
            <span className="text-teal-400 font-bold text-sm" data-id="element-492" data-mp="3bx3wiblieoxq">snakker</span>
          </div>
          <h3 className="text-sm md:text-base font-bold text-white" data-id="element-493" data-mp="3gv5o5kx03wkt">
            4-Year <span className="text-teal-400" data-id="element-494" data-mp="nzdmtvwzv5mg">Revenue</span> & Net Result
          </h3>
          <div className="flex items-center gap-3 text-[10px]" data-id="element-495" data-mp="j1lrerx551di">
            <div className="flex items-center gap-1.5" data-id="element-496" data-mp="j3f1kh6yq3q9">
              <div className="w-2.5 h-2.5 rounded-sm bg-teal-400" data-id="element-497" data-mp="x1wo4bl4ta6l" />
              <span className="text-white/70" data-id="element-498" data-mp="s20f1xxkusk6">Revenue (DKK M)</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-499" data-mp="16z1aabez95ne">
              <div className="w-2.5 h-2.5 rounded-sm bg-amber-400" data-id="element-500" data-mp="16xgekigxfgny" />
              <span className="text-white/70" data-id="element-501" data-mp="120pznyguyr44">Net Result</span>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 mt-2" data-id="element-502" data-mp="1fq9wknnrcu5i">
          <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet" data-id="element-503" data-mp="1ftfpkdzicg6i">
            <defs data-id="element-504" data-mp="1pqajejku5seg">
              <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-505" data-mp="1ppsg52zradx1">
                <stop offset="0%" stopColor="#00D4AA" stopOpacity="0.95" data-id="element-506" data-mp="1krh4qnttbvcp" />
                <stop offset="100%" stopColor="#00D4AA" stopOpacity="0.55" data-id="element-507" data-mp="24bofimpk0zu9" />
              </linearGradient>
            </defs>

            {/* Y grid + labels */}
            {yTicks.map(t => <g key={t} data-id="element-508" data-mp="1uejpucwi2m8p">
                <line x1={padL} y1={yToPx(t)} x2={chartW - padR} y2={yToPx(t)} stroke="#cbd5e1" strokeOpacity={t === 0 ? 0.6 : 0.25} strokeDasharray={t === 0 ? '0' : '3 4'} strokeWidth="0.7" data-id="element-509" data-mp="1ud0bwsybgna3" />
                <text x={padL - 8} y={yToPx(t) + 3} textAnchor="end" className="fill-white/45 text-[10px]" data-id="element-510" data-mp="1bnk7vvnfacox">
                  {t}M
                </text>
              </g>)}

            {/* Zero baseline emphasized */}
            <line x1={padL} y1={zeroY} x2={chartW - padR} y2={zeroY} stroke="#cbd5e1" strokeOpacity="0.7" strokeWidth="1" data-id="element-511" data-mp="16oz3rl51v8v7" />

            {/* Revenue bars */}
            {YEARS.map((y, i) => {
            const top = yToPx(y.revenue);
            const h = zeroY - top;
            return <motion.rect key={y.year} x={colXs[i] - barWidth / 2} y={top} width={barWidth} height={h} rx={4} fill="url(#revGrad)" initial={{
              opacity: 0,
              scaleY: 0
            }} animate={{
              opacity: stage >= 3 ? 1 : 0,
              scaleY: stage >= 3 ? 1 : 0
            }} transition={{
              duration: 0.6,
              delay: 0.1 + i * 0.12,
              ease: 'easeOut'
            }} style={{
              transformOrigin: `${colXs[i]}px ${zeroY}px`
            }} data-id="element-512" data-mp="187h86rxyc3hf" data-mpi={i} />;
          })}

            {/* Revenue value labels above bars */}
            {YEARS.map((y, i) => <motion.text key={`rev-${y.year}`} x={colXs[i]} y={yToPx(y.revenue) - 8} textAnchor="middle" className="fill-teal-300 text-[10px] font-bold tabular-nums" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            delay: 0.2 + i * 0.08
          }} data-id="element-513" data-mp="314ct615hh00f" data-mpi={i}>
                {y.revenue}M
              </motion.text>)}

            {/* Net result line */}
            <motion.path d={netLinePath} fill="none" stroke="#FBBF24" strokeWidth="2.4" strokeLinecap="round" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.6,
            ease: 'easeInOut',
            delay: 0.3
          }} data-id="element-514" data-mp="1unhk33bfhu0b" />

            {/* Net result dots */}
            {YEARS.map((y, i) => <motion.g key={`net-${y.year}`} initial={{
            opacity: 0,
            scale: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0,
            scale: stage >= 4 ? 1 : 0
          }} transition={{
            delay: 0.6 + i * 0.1,
            type: 'spring',
            stiffness: 240
          }} data-id="element-515" data-mp="6df5uvmxxabs" data-mpi={i}>
                <circle cx={colXs[i]} cy={yToPx(y.netResult)} r={4} fill={y.netResult >= 0 ? '#FBBF24' : '#94a3b8'} stroke="#0b1426" strokeWidth="2" data-id="element-516" data-mp="3ooabw7fglpvy" />
                <text x={colXs[i]} y={yToPx(y.netResult) + (y.netResult >= 0 ? -10 : 16)} textAnchor="middle" className={`text-[9px] font-bold tabular-nums ${y.netResult >= 0 ? 'fill-amber-300' : 'fill-white/60'}`} data-id="element-517" data-mp="1wszld4zy3gb8">
                  {y.netResult > 0 ? '+' : ''}
                  {y.netResult}M
                </text>
              </motion.g>)}

            {/* Profitability inflection marker between 2026 & 2027 */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            delay: 1.4
          }} data-id="element-518" data-mp="195howw5qju1x">
              <line x1={(colXs[0] + colXs[1]) / 2} y1={padT} x2={(colXs[0] + colXs[1]) / 2} y2={chartH - padB} stroke="#10b981" strokeOpacity="0.45" strokeDasharray="4 4" strokeWidth="1" data-id="element-519" data-mp="pioc1d5vh29j" />
              <rect x={(colXs[0] + colXs[1]) / 2 - 38} y={padT - 2} width="76" height="18" rx="9" fill="#10b981" fillOpacity="0.18" stroke="#10b981" strokeOpacity="0.5" data-id="element-520" data-mp="kk37j1jl7wfa" />
              <text x={(colXs[0] + colXs[1]) / 2} y={padT + 10} textAnchor="middle" className="fill-emerald-300 text-[9px] font-bold uppercase tracking-wider" data-id="element-521" data-mp="zehufd69vpz8">
                Breakeven
              </text>
            </motion.g>

            {/* X labels — year + draft/budget */}
            {YEARS.map((y, i) => <g key={`x-${y.year}`} data-id="element-522" data-mp="3gn2mibqi39g0" data-mpi={i}>
                <text x={colXs[i]} y={chartH - padB + 16} textAnchor="middle" className="fill-white/85 text-[11px] font-bold" data-id="element-523" data-mp="3bq2mahgc10wt">
                  {y.year}
                </text>
                <text x={colXs[i]} y={chartH - padB + 28} textAnchor="middle" className="fill-white/40 text-[9px] uppercase tracking-wider" data-id="element-524" data-mp="3lhp1spzpdm5d">
                  {y.label}
                </text>
              </g>)}
          </svg>
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 5 ? 1 : 0
    }} className="mt-8 flex justify-end" data-id="element-525" data-mp="32o2zt9mjt4mh"></motion.div>
    </div>;
}